import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnViewportChange } from 'reactflow';
import { Save, X, CheckCircle, LayoutGrid } from 'lucide-react';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { useUIStore } from '../../store';
import { useNotificationStore } from '../../store';
import { apiService } from '../../services/api';
import { buildPipelineSteps } from '../../utils/executionFlow';
import { autoLayoutNodes } from '../../utils/autoLayout';
import { formatApiError } from '../../utils/errorFormatter';
import { ValidationResultsModal } from './ValidationResultsModal';
import { CancelConfirmationModal } from './CancelConfirmationModal';
import { PipelineStatus } from '../../types/pipeline';

export const PipelineToolbar: React.FC = () => {
  const navigate = useNavigate();
  const theme = useUIStore((state) => state.theme);
  const addNotification = useNotificationStore((state) => state.addNotification);

  // Store state
  const {
    pipelineId,
    pipelineName,
    pipelineDescription,
    pipelineVersion,
    pipelineStatus,
    nodes,
    edges,
    isDirty,
    isSaving,
    setMetadata,
    setNodes,
    revertChanges,
    markClean,
    saveOriginalState,
    setSaving,
    setValidationResults: setValidationResultsInStore,
  } = usePipelineBuilderStore();

  // Local state for modals
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResults, setValidationResults] = useState<{
    is_valid: boolean;
    errors: any[];
    warnings: any[];
  } | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // State for expanded description textarea
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Zoom level - reactive to viewport changes
  const [zoomLevel, setZoomLevel] = useState(100);

  // Update zoom level when viewport changes
  useOnViewportChange({
    onChange: (viewport) => {
      setZoomLevel(Math.round(viewport.zoom * 100));
    },
  });

  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMetadata({ pipelineName: e.target.value });
  };

  // Handle description change
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMetadata({ pipelineDescription: e.target.value });
  };

  // Handle status change
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMetadata({ pipelineStatus: e.target.value as any });
  };

  // Check if there are any processor nodes (excluding system input)
  const hasProcessorNodes = nodes.filter(n => n.id !== 'input').length > 0;

  // Handle validate
  const handleValidate = async () => {
    try {
      const steps = buildPipelineSteps(nodes, edges);
      const results = await apiService.validatePipeline({
        steps,
        name: pipelineName || undefined,
      });
      // Store results in both local state (for modal) and store (for nodes)
      setValidationResults(results);
      setValidationResultsInStore(results);
      setShowValidationModal(true);
    } catch (error: any) {
      addNotification(
        formatApiError(error),
        'error'
      );
    }
  };

  // Handle auto-layout
  const handleAutoLayout = () => {
    const updatedNodes = autoLayoutNodes(nodes, edges);
    setNodes(updatedNodes);
    addNotification('Nodes arranged automatically', 'success');
  };

  // Handle cancel
  const handleCancel = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      navigate('/pipelines');
    }
  };

  // Handle cancel confirmation
  const handleConfirmCancel = () => {
    revertChanges();
    setShowCancelModal(false);
    navigate('/pipelines');
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);

    try {
      const steps = buildPipelineSteps(nodes, edges);

      // Validate the pipeline before saving
      const validationResults = await apiService.validatePipeline({
        steps,
        name: pipelineName || undefined,
      });

      // Store validation results in store (for nodes)
      setValidationResultsInStore(validationResults);

      // Determine status based on validation results
      let newStatus: PipelineStatus = pipelineStatus;
      if (validationResults.errors.length > 0 || validationResults.warnings.length > 0) {
        newStatus = 'ERROR';
      }

      let response;
      if (pipelineId === null) {
        // Create new pipeline (POST)
        response = await apiService.createPipeline({
          name: pipelineName || 'Untitled Pipeline',
          description: pipelineDescription || undefined,
          version: pipelineVersion,
          status: newStatus, // Include status from dropdown
          steps: steps as any, // Steps are validated by backend
        });
        addNotification('Pipeline created successfully', 'success');
      } else {
        // Update existing pipeline (PUT)
        // Note: Don't send version - let backend auto-increment it
        response = await apiService.updatePipeline(pipelineId, {
          name: pipelineName,
          description: pipelineDescription || undefined,
          status: newStatus,
          steps: steps as any, // Steps are validated by backend
        });
        addNotification('Pipeline updated successfully', 'success');
      }

      // Update store with returned data (including new version from backend)
      setMetadata({
        pipelineVersion: response.version,
        pipelineStatus: response.status,
      });

      // Mark as clean and save new original state
      markClean();
      saveOriginalState();

      // Navigate back to pipelines list
      navigate('/pipelines');
    } catch (error: any) {
      addNotification(
        formatApiError(error),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}
      >
        {/* Left section: Metadata */}
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={pipelineName}
            onChange={handleNameChange}
            placeholder="Untitled Pipeline"
            className={`px-3 py-1.5 rounded-lg border font-medium text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            style={{ minWidth: '200px' }}
          />

          {isDescriptionExpanded ? (
            <textarea
              value={pipelineDescription || ''}
              onChange={handleDescriptionChange}
              onBlur={() => setIsDescriptionExpanded(false)}
              placeholder="Description"
              autoFocus
              rows={4}
              className={`px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              style={{ minWidth: '350px', maxWidth: '500px' }}
            />
          ) : (
            <input
              type="text"
              value={pipelineDescription || ''}
              onChange={handleDescriptionChange}
              onClick={() => setIsDescriptionExpanded(true)}
              placeholder="Description (click to expand)"
              className={`px-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-text ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              style={{ minWidth: '250px' }}
            />
          )}

          <span
            className={`text-sm font-medium ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Version: {pipelineVersion}
          </span>

          <select
            value={pipelineStatus}
            onChange={handleStatusChange}
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="DRAFT">DRAFT</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="DISABLED">DISABLED</option>
            <option value="ERROR">ERROR</option>
          </select>
        </div>

        {/* Right section: Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleValidate}
            className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center ${
              theme === 'dark'
                ? 'bg-purple-600 hover:bg-purple-700 text-white'
                : 'bg-purple-500 hover:bg-purple-600 text-white'
            }`}
          >
            <CheckCircle className="w-4 h-4 mr-1.5" />
            Validate
          </button>

          <button
            onClick={handleAutoLayout}
            className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center ${
              theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            <LayoutGrid className="w-4 h-4 mr-1.5" />
            Auto-Layout
          </button>

          <span
            className={`text-sm font-medium px-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Zoom: {zoomLevel}%
          </span>

          <button
            onClick={handleCancel}
            className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center ${
              theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            <X className="w-4 h-4 mr-1.5" />
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving || !hasProcessorNodes}
            className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center ${
              !isDirty || isSaving || !hasProcessorNodes
                ? theme === 'dark'
                  ? 'bg-blue-800 text-blue-400 cursor-not-allowed'
                  : 'bg-blue-200 text-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Save className="w-4 h-4 mr-1.5" />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Modals */}
      {validationResults && (
        <ValidationResultsModal
          isOpen={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          is_valid={validationResults.is_valid}
          errors={validationResults.errors}
          warnings={validationResults.warnings}
        />
      )}

      <CancelConfirmationModal
        isOpen={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

